// src/api/chat.js
//
// Thin fetch wrapper around Tixaru's customer support chat API
// (`/api/v1/my-chat/*`) -- one conversation per customer, which can be about
// an order, and from which a numbered ticket can be raised (the ticket is
// then discussed in the same chat). Same shape and auth as src/api/tickets.js:
// the signed-in customer's own Firebase ID token goes as a Bearer token and
// REACT_APP_TIXARU_API_URL must be set at CRA build time.

import { auth } from "../firebase";

const API_BASE = process.env.REACT_APP_TIXARU_API_URL;
const CHAT_BASE = `${API_BASE || ""}/api/v1/my-chat`;

export const TICKET_CATEGORIES = [
  { value: "order_issue", label: "Order issue" },
  { value: "product_support", label: "Product support" },
  { value: "warranty_repair", label: "Warranty / repair" },
  { value: "general_inquiry", label: "General inquiry" },
  { value: "other", label: "Other" },
];

export const TICKET_STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

async function parseErrorDetail(res) {
  try {
    const data = await res.json();
    if (data) {
      if (typeof data.detail === "string") return data.detail;
      if (Array.isArray(data.detail)) return data.detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      if (data.detail) return JSON.stringify(data.detail);
    }
  } catch (err) {
    // Body wasn't JSON -- fall through.
  }
  return `Request failed (${res.status})`;
}

async function authedFetch(path, { method = "GET", body, isFormData = false, forceRefresh = false } = {}) {
  if (!API_BASE) {
    throw new Error("Chat isn't configured yet (missing REACT_APP_TIXARU_API_URL). Please try again later.");
  }
  if (!auth.currentUser) {
    throw new Error("You need to be signed in to do that.");
  }
  const token = await auth.currentUser.getIdToken(forceRefresh);
  const headers = { Authorization: `Bearer ${token}` };
  let payload = body;
  if (body !== undefined && !isFormData) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(`${CHAT_BASE}${path}`, { method, headers, body: payload });
  } catch (err) {
    throw new Error("Could not reach the support server. Please check your connection and try again.");
  }
  // ID tokens expire hourly: one retry with a refreshed token.
  if (res.status === 401 && !forceRefresh) {
    return authedFetch(path, { method, body, isFormData, forceRefresh: true });
  }
  if (!res.ok) {
    throw new Error(await parseErrorDetail(res));
  }
  return res;
}

async function request(path, options) {
  const res = await authedFetch(path, options);
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const getChat = () => request("");

export const getUnreadCount = () => request("/unread-count");

export const markRead = () => request("/read", { method: "POST" });

export const listMessages = () => request("/messages");

// ticketId: send into one of the customer's tickets; orderId: tag a general
// message with one of their orders. hasAttachment lets the text be blank when
// the message only carries a file.
export const sendMessage = ({ body, ticketId, orderId, hasAttachment }) =>
  request("/messages", {
    method: "POST",
    body: {
      body: body || "",
      ticket_id: ticketId || undefined,
      related_order_id: orderId || undefined,
      has_attachment: !!hasAttachment,
    },
  });

export const listTickets = () => request("/tickets");

// category: "order_issue" | "product_support" | "warranty_repair" | "general_inquiry" | "other"
export const raiseTicket = ({ subject, description, category, orderId }) =>
  request("/tickets", {
    method: "POST",
    body: { subject, description, category, related_order_id: orderId || undefined },
  });

export function uploadAttachment(messageId, file) {
  const formData = new FormData();
  formData.append("file", file);
  return request(`/attachments?message_id=${encodeURIComponent(messageId)}`, {
    method: "POST",
    body: formData,
    isFormData: true,
  });
}

// The file route needs the Authorization header too, so it can't be a plain
// link: fetch the bytes and hand back an object URL (caller revokes it).
export async function fetchAttachmentBlobUrl(attachmentId) {
  const res = await authedFetch(`/attachments/${attachmentId}/file`);
  return URL.createObjectURL(await res.blob());
}
